import React, { useContext, useEffect, useState } from 'react'
import { Table, Button, Typography, Affix, App, Modal, Form, Space, Switch, Image, Avatar } from 'antd'
import { isMobileOnly, isDesktop } from "react-device-detect"
import { useNavigate, Link } from "react-router-dom"
import _ from "lodash"
import dayjs, { Dayjs } from 'dayjs'
import { saveAs } from "file-saver"
import moment from 'moment'
import { jsPDF } from "jspdf";
import { FaPlus, FaEdit, FaDownload, FaImage, FaTrash, FaSearch, FaFileExcel } from "react-icons/fa"
import { API, Enum, Font } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'

// Components
import Filter from "./filter"

const currency = new Intl.NumberFormat()
const { Pages: { TicketManagement: { Ticket: TicketLanguage } }, Enum: EnumLanguage, Share, Message: MessageLanguage } = language
const {  TICKET_MANAGEMENT_TICKET_PAGINATE, TICKET_MANAGEMENT_TICKET_REMOVE, TICKET_MANAGEMENT_TICKET_UPDATE, TICKET_MANAGEMENT_TICKET_EXCEL } = API
const { Message, Payment, Component } = Enum
const { IRANSans, IRANSansFaNum } = Font

interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CityType {
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface UserType {
    id: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
    mobile: string,
    email?: string,
    createdAt: Date,
    updatedAt: Date,
}

interface CompanyType {
    row: number,
    id: string,
    key: string,
    number: string,
    position: number,
    name: string,
    tel: string,
    cities: CityType[],
    user: UserType,
    publish: boolean,
    tickets?: { count: number, seat: number }[]
    seatOne?: number,
    seatTwo?: number,
    seatThree?: number,
    seatLeft?: number,
    carLeft?: number,
    countService?: number,
    priority?: number,
    createdAt: Date,
    updatedAt: Date,
}

interface TicketType {
    row: number,
    id: string,
    key: string,
    number: string,
    user: UserType,
    operator?: UserType,
    destination: CityType,
    company: CompanyType,
    seat: number,
    amount: number,
    outCycle: boolean,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface FiltersType {
    number?: string,
    firstName?: string,
    lastName?: string,
    mobile?: string,
    nationalCode?: string,
    destination?: string,
    company?: string,
    createdAt?: Dayjs,
    updatedAt?: Dayjs,
}

export default function Index() {

    const [formModal] = Form.useForm()
    const navigate = useNavigate()
    const { accessToken, baseURL } = useContext(GlobalContext)
    const [tickets, setTickets] = useState<TicketType[]>([])
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [component, setComponent] = useState<string|null>(null)
    const [filters, setFilters] = useState<FiltersType>({})
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    })
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp()

    const ticketColumns: ColumnsType<TicketType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: TicketLanguage.number,
            dataIndex: "number",
            key: "number",
            sorter: true,
        },
        {
            title: TicketLanguage.firstName,
            dataIndex: ["user", "firstName"],
            key: "firstName",
        },
        {
            title: TicketLanguage.lastName,
            dataIndex: ["user", "lastName"],
            key: "lastName",
        },
        {
            title: TicketLanguage.mobile,
            dataIndex: ["user", "mobile"],
            key: "mobile",
            render: (text, record) => text && <Typography.Link href={`tel:+98${text}`}>0{text}</Typography.Link>
        },
        {
            title: TicketLanguage.nationalCode,
            dataIndex: ["user", "nationalCode"],
            key: "nationalCode",
        },
        {
            title: TicketLanguage.destination,
            dataIndex: "destination",
            render: (text, record) => text && `${text.name} (${text.province.name})`
        },
        {
            title: TicketLanguage.seat,
            dataIndex: "seat",
            key: "seat",
        },
        {
            title: TicketLanguage.company,
            dataIndex: "company",
            key: "company",
            render: (text: CompanyType, record) => text && text.name
        },
        {
            title: TicketLanguage.amount,
            dataIndex: 'amount',
            key: 'amount',
            render: (text, record) => text.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' ' + Share.rial
        },
        {
            title: TicketLanguage.operator,
            dataIndex: "operator",
            key: "operator",
            render: (text, record) => text && `${text.firstName} ${text.lastName}`,
        },
        {
            title: TicketLanguage.createdAt,
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('HH:mm YYYY/MM/DD')
        },
        {
            title: TicketLanguage.updatedAt,
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('HH:mm YYYY/MM/DD')
        },
        {
            title: TicketLanguage.outCycle,
            dataIndex: "outCycle",
            key: "outCycle",
            render: (text: CompanyType, record) => text ? Share.yes : Share.no
        },
        {
            title: Share.publish,
            dataIndex: 'publish',
            key: 'publish',
            render: (text, { id }) =>  (
                <Switch
                    checkedChildren={Share.active}
                    unCheckedChildren={Share.inactive}
                    checked={text}
                    style={{width:75}}
                    onClick={() => {
                        setLoadingRecords(true)
                        axios.patch(TICKET_MANAGEMENT_TICKET_UPDATE + id, { publish: !text }).then((response: {}) => {
                            message.success(MessageLanguage[Message.UPDATE_RECORD]).then(response => response)
                            const { current, pageSize } = pagination
                            getTickets(filters, { page: current, limit: pageSize })
                        }).catch((error: AxiosError) => {
                            const { response: { data } } = error;
                            // @ts-ignore
                            message.error(MessageLanguage[data.message]).then(response => response)
                            setLoadingRecords(false)
                        })
                    }}
                />
            )
        },
        {
            title: Share.edit,
            dataIndex: "edit",
            key: "edit",
            render: (text, record) =>  <Link to={'/ticket-management/ticket/edit/' + record.id}><FaEdit size={20} /></Link>
        },
        {
            title: Share.delete,
            dataIndex: "delete",
            key: "delete",
            render: (text, record) =>  (
                <Typography.Link onClick={() => modal.confirm({
                    title: Share.deleteConfirmTitle,
                    okText: Share.deleteConfirmOk,
                    okType: 'danger',
                    cancelText: Share.deleteConfirmCancel,
                    onOk() {
                        setLoadingRecords(true)
                        axios.delete(TICKET_MANAGEMENT_TICKET_REMOVE + record.id).then((response: {}) => {
                            const { total = 1, pageSize = 1, current = 1 } = pagination
                            const params = {
                                page: total % pageSize !== 1 ? current : current - 1,
                                limit: pageSize,
                            }
                            getTickets(filters, params)
                            message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                        }).catch((error: AxiosError) => {
                            const { response: { data } } = error
                            // @ts-ignore
                            message.error(MessageLanguage[data.message]).then(response => response )
                            setLoadingRecords(false)
                        })
                    }
                })}><FaTrash size={20} /></Typography.Link>
            )
        }
    ]

    const getTickets = (filters: FiltersType, params: AntTableParams) => {
        setLoadingRecords(true)
        setFilters(filters)
        axios.post(TICKET_MANAGEMENT_TICKET_PAGINATE, filters, { params }).then((response: AxiosResponsePaginate) => {
            const { data: { docs: tickets, page: current, limit: pageSize, totalDocs: total } } = response
            let row = (current - 1) * pageSize
            setTickets(tickets.map<TicketType>((ticket: TicketType) => ({ ...ticket, row: ++row, key: ticket.id })))
            setLoadingRecords(false)
            setPagination({
                ...pagination,
                current,
                pageSize,
                total,
            })
        }).catch((error: AxiosError) => {
            const { response: { data } } = error
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setLoadingRecords(false)
        })
    }

    const exportExcel = (filters: FiltersType) => {
        axios.post(TICKET_MANAGEMENT_TICKET_EXCEL, filters, { responseType: 'blob' }).then((response: any) => {
            const { data } = response;
            const blob = new Blob([data], {
                type: response.headers['content-type'],
            });
            saveAs(blob, response.headers['x-file-name']);
        }).catch((error: AxiosError) => {
            const { response: { data } } = error
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
        })
    }

    useEffect(() => {
        const { current, pageSize } = pagination
        getTickets(filters, { page: current, limit: pageSize })
    }, [])

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filtersTable: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getTickets(filters, { page: current, limit: pageSize, sort })
    }

    return (
        <>
            <Modal destroyOnClose={true} title={
                (component === Component.Filter) ? Share.search :
                    Share?.view
            }  cancelButtonProps={{
                style: {display:(component === Component.Filter) ? undefined : 'none'},
            }} cancelText={Share.cancel} okText={
                (component === Component.Filter) ? Share.search :
                    Share.close
            } width={
                (component === Component.Filter) ? 500 :
                    500
            } open={isModalOpen} onOk={() => {
                if (component === Component.Filter) {
                    formModal.submit()
                }
                setComponent(null)
                setIsModalOpen(false)
            }} onCancel={() => {
                setComponent(null)
                setIsModalOpen(false)
            }}>
                { (component === Component.Filter) && <Filter Form={Form} form={formModal} filters={filters} setIsModalOpen={setIsModalOpen} getTickets={getTickets} pagination={pagination} /> }
            </Modal>
            <Table
                columns={ticketColumns}
                dataSource={tickets}
                pagination={pagination}
                loading={loadingRecords}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
            <Affix offsetBottom={30}>
                <Space.Compact block style={{marginTop:'10px'}}>
                    <Button
                        key='add'
                        type="primary"
                        icon={<FaPlus />}
                        style={{height:'40px'}}
                        onClick={() => navigate('/ticket-management/ticket/add')}>
                        {TicketLanguage.addTicket}
                    </Button>
                    <Button
                        key='search'
                        icon={<FaSearch />}
                        style={{height:'40px'}}
                        onClick={() => {
                        setComponent(Component.Filter)
                        setIsModalOpen(true)
                    }}>
                        {Share.search}
                    </Button>
                    <Button
                        key='excel'
                        icon={<FaFileExcel />}
                        style={{height:'40px'}}
                        danger
                        onClick={() =>  _.every(filters, _.isNil) ? modal.info({
                            title: Share.excel,
                            content: TicketLanguage.excel,
                            closable: true,
                            maskClosable: true,
                            width: 400,
                            okText: Share.confirm,
                            onOk() {
                                setComponent(Component.Filter)
                                setIsModalOpen(true)
                            }
                        }) : exportExcel(filters)}>
                        {Share.excel}
                    </Button>
                </Space.Compact>
            </Affix>
        </>
    )
}
